import { useTranslation } from "react-i18next";
import { uiText } from "@/lib/i18n/uiText";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";

const MIN_AGE = 18;

function ageFromDob(dob: string): number {
  const birth = new Date(dob);
  const now = new Date();
  let age = now.getFullYear() - birth.getFullYear();
  const m = now.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < birth.getDate())) age--;
  return age;
}

/**
 * Post-sign-in completion step.
 *
 * Sign-in itself is Google-only, so the two things Google does not give us —
 * date of birth (the 18+ age gate) and a referral code — are captured here on
 * the first authenticated visit. The dialog cannot be dismissed until a valid
 * date of birth is stored, which keeps the age gate intact.
 */
export function ProfileCompletionGate() {
  // Re-render once the selected language finishes loading.
  useTranslation();
  const { user, isLoading } = useAuth();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [dob, setDob] = useState("");
  const [refCode, setRefCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isLoading || !user) return;
    let active = true;
    (async () => {
      const { data } = await supabase
        .from("profiles")
        .select("date_of_birth, referred_by_code")
        .eq("id", user.id)
        .maybeSingle();
      if (!active) return;
      if (!data?.date_of_birth) {
        try {
          const stored = localStorage.getItem("statlign_ref_code");
          if (stored) setRefCode(stored);
        } catch {
          /* ignore storage errors */
        }
        setOpen(true);
      }
    })();
    return () => {
      active = false;
    };
  }, [user, isLoading]);

  const submit = async () => {
    setError(null);
    if (!user) return;
    if (!dob) {
      setError(uiText("Please enter your date of birth."));
      return;
    }
    if (Number.isNaN(new Date(dob).getTime())) {
      setError(uiText("That date does not look valid."));
      return;
    }
    if (ageFromDob(dob) < MIN_AGE) {
      setError(uiText("You must be at least {{v0}} years old to use Statlign.", { v0: MIN_AGE }));
      return;
    }

    setSaving(true);
    const code = refCode.trim();
    const patch = {
      id: user.id,
      date_of_birth: dob,
      ...(code ? { referred_by_code: code } : {}),
    };

    const { error: upsertError } = await supabase
      .from("profiles")
      .upsert(patch, { onConflict: "id" });
    setSaving(false);

    if (upsertError) {
      setError(
        upsertError.message.includes("AGE_RESTRICTION")
          ? uiText("You must be at least {{v0}} years old to use Statlign.", { v0: MIN_AGE })
          : upsertError.message,
      );
      return;
    }

    try {
      localStorage.removeItem("statlign_ref_code");
    } catch {
      /* ignore */
    }
    setOpen(false);
    toast({
      title: uiText("Profile complete"),
      description: code
        ? uiText("Thanks — your referral code was applied.")
        : uiText("Thanks — you're all set."),
    });
  };

  if (!user) return null;

  return (
    <Dialog open={open}>
      <DialogContent
        className="sm:max-w-md"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>{uiText("Finish setting up your account")}</DialogTitle>
          <DialogDescription>
             {uiText("Statlign is strictly for adults aged")} {MIN_AGE}{uiText("+. Confirm your date of birth to continue — add a referral code if a member invited you.")} </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="completion-dob">{uiText("Date of birth")}</Label>
            <Input
              id="completion-dob"
              type="date"
              value={dob}
              onChange={(e) => setDob(e.target.value)}
              max={new Date().toISOString().slice(0, 10)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="completion-ref">{uiText("Referral code (optional)")}</Label>
            <Input
              id="completion-ref"
              value={refCode}
              onChange={(e) => setRefCode(e.target.value)}
              placeholder={uiText("e.g. STAT-1234")}
            />
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <p className="text-xs text-muted-foreground">
             {uiText("Betting carries real financial risk. Nothing on Statlign is a guaranteed win — please gamble responsibly.")} </p>
        </div>

        <DialogFooter>
          <Button onClick={submit} disabled={saving} className="w-full">
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
             {uiText("Continue")} </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
